/**
 * The answer-first read seam. For a GET that the platform's offline answer capability can
 * answer, this module returns the local answer at once. It also sends the real
 * request in the background. If the server response differs, the module
 * invalidates only the related React Query data.
 *
 * The request interceptor in `axios.ts` calls this module after it captures the
 * prefs generation. A platform without an offline answer capability does not use this path.
 * Non-GET, binary, role-dependent, and unsupported requests continue to the
 * network. `offline_answer` remains the final authority for data presence.
 *
 * A generation check after each asynchronous boundary prevents an answer from
 * one organization from entering another organization's query cache. The Rust
 * command derives its tenant from the current `AuthState`; the renderer does
 * not supply an organization identifier.
 *
 * The background comparison is structural and is independent of object-key
 * order. A correction invalidates the narrowest stable query-key group for the
 * request family. It does not invalidate the complete query cache.
 *
 * Correction invalidations are batched into one microtask. Active queries then
 * refetch. Inactive queries refetch when they become active.
 */
import type { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios";
import { AxiosHeaders } from "axios";
import type { QueryClient } from "react-query";
import type { PrefsCapability } from "../platform/Platform";
import { platformRef } from "../platform/platformRef";
import { isDebugEnabled } from "../hooks/configuration";
import { PrimeQueryKeys } from "../shared/constants";

export type NativeAnswerFirstConfig = AxiosRequestConfig & {
    _nativeOfflineCacheKey?: string | null;
    /** Same field axios.ts's request interceptor captures on `config` before this
     *  module ever sees it (see that file's "Capture both ownership fields before the
     *  first await" comment) -- the tenant fence for the local DAO answer below. */
    _nativeOfflineGeneration?: number;
    _nativeAnswerFirstBackgroundRefetch?: boolean;
};

/** Response header marking an answer-first local hit -- read by callers/tests that
 *  want to distinguish it, and by `axios.ts`'s own response interceptor to skip
 *  treating a local (non-network) resolution as evidence the network is reachable. */
export const LOCAL_ANSWER_HEADER = "x-primepoint-local-answer";

export function isLocalAnswerResponse(response: AxiosResponse): boolean {
    const headers = response.headers as unknown;
    if (headers instanceof AxiosHeaders) {
        return headers.get(LOCAL_ANSWER_HEADER) === "true";
    }
    if (headers && typeof headers === "object") {
        return (headers as Record<string, unknown>)[LOCAL_ANSWER_HEADER] === "true";
    }
    return false;
}

/** Which of `offline_answer`'s (Rust) served families a pathname belongs to, or
 *  `null` if it can never be answered locally -- see `desktop/src-tauri/src/sync/
 *  offline_answer.rs`'s own module doc ("Families served") and its `answer()` match
 *  arms, which this mirrors segment-for-segment (not a loose string prefix: `/api/ai/
 *  document/{id}/section_groups/text_matches`, `/destination_index`, and every other
 *  `/api/ai/document/{id}/*` sub-route this app also calls are real endpoints, but NONE
 *  of them are servable here, so a bare `/api/ai/document/` prefix would still pay the
 *  IPC round trip for exactly the misses this allowlist exists to skip). Single source
 *  of truth for both the request-time gate (`isAnswerFirstEligiblePath`) and the
 *  correction-time invalidation predicate (`invalidateCorrectedQueries`) below -- a
 *  future family change on the Rust side has exactly one place here to update in step. */
export type AnswerFirstFamily =
    | { kind: "file"; fileId: string }
    | { kind: "sheetBootstrap"; fileId: string }
    | { kind: "document"; documentId: string }
    | { kind: "documentSectionGroups"; documentId: string }
    | { kind: "sectionGroupsByType" }
    | { kind: "documentsList" }
    | { kind: "folders" }
    | { kind: "folderedDocuments" }
    | { kind: "rfisList" }
    | { kind: "rfiDetail"; documentId: string }
    | { kind: "submittalDetail"; documentId: string }
    | { kind: "areas" }
    | { kind: "revisions" }
    | { kind: "projectSettingDisciplineMap" }
    | { kind: "scheduleProjects"; fileId: string }
    | { kind: "scheduleWbs"; fileId: string; projectId: string | null }
    | { kind: "scheduleWbsChildren"; wbsId: string }
    | { kind: "conversationsList" };

/** The string every family's `kind` takes -- mirrored 1:1 against the Rust match arms
 *  `sync/offline_answer.rs::answer` routes on (`pub const FAMILIES` there). The lockstep
 *  selftest (`offline-answer:selftest`, round 4 item D) greps the Rust source for its own
 *  `FAMILIES` array and asserts this array's contents are the same set -- a family added on
 *  one side and forgotten on the other fails that check loudly instead of silently drifting.
 *  Order does not matter to the selftest (set comparison), only membership. */
export const FAMILIES: readonly AnswerFirstFamily["kind"][] = [
    "file",
    "sheetBootstrap",
    "document",
    "documentSectionGroups",
    "sectionGroupsByType",
    "documentsList",
    "folders",
    "folderedDocuments",
    "rfisList",
    "rfiDetail",
    "submittalDetail",
    "areas",
    "revisions",
    "projectSettingDisciplineMap",
    "scheduleProjects",
    "scheduleWbs",
    "scheduleWbsChildren",
    "conversationsList",
] as const;

/** `FOLDER_SCOPED_QUERY_KEYS` (`sync/offline_answer.rs`) mirrored here -- the query keys
 *  that mark `GET /api/ai/documents` as its folder-scoped variant (`folderedDocuments`)
 *  rather than the plain list (`documentsList`). Accepts a plain object or
 *  `URLSearchParams`, the same two shapes axios `params` can legally be (matching
 *  `readDocTypeQueryParam`'s own handling below). */
function readStringQueryParam(query: unknown, key: string): string | null {
    if (query instanceof URLSearchParams) return query.get(key);
    if (query && typeof query === "object") {
        const value = (query as Record<string, unknown>)[key];
        return typeof value === "string" ? value : null;
    }
    return null;
}

/** Merges query parameters embedded in `config.url` with axios `config.params`.
 *  Most callers use one shape. Schedule WBS requests use the URL shape. */
function answerFirstQuery(nativeUrl: URL, params: unknown): unknown {
    if (!nativeUrl.search) return params;
    const query: Record<string, unknown> = Object.fromEntries(nativeUrl.searchParams.entries());
    if (params instanceof URLSearchParams) {
        params.forEach((value, key) => {
            query[key] = value;
        });
    } else if (params && typeof params === "object") {
        Object.assign(query, params);
    }
    return query;
}

function isFolderScopedQuery(query: unknown): boolean {
    const keys = ["folder_id", "library", "root_only"];
    if (query instanceof URLSearchParams) return keys.some((key) => query.has(key));
    if (query && typeof query === "object") {
        return keys.some((key) => (query as Record<string, unknown>)[key] !== undefined);
    }
    return false;
}

/** The query selects a documents-list variant and carries the schedule project
 *  identity when that value is in the URL query string. */
export function classifyAnswerFirstPath(pathname: string, query?: unknown): AnswerFirstFamily | null {
    const segments = pathname.split("/").filter((segment) => segment.length > 0);
    if (segments[0] !== "api" || segments[1] !== "ai") return null;
    if (segments.length === 4 && segments[2] === "file") {
        return { kind: "file", fileId: segments[3] };
    }
    if (segments.length === 5 && segments[2] === "file" && segments[4] === "sheet_bootstrap") {
        return { kind: "sheetBootstrap", fileId: segments[3] };
    }
    if (segments.length === 4 && segments[2] === "document") {
        // The sheet index is a web read: a host that answers from its own store builds the
        // list from the sheet rows it holds (`useSheetIndex`) and never asks for it.
        if (segments[3] === "sheet_index") return null;
        return segments[3] === "section_groups_by_type"
            ? { kind: "sectionGroupsByType" }
            : { kind: "document", documentId: segments[3] };
    }
    if (segments.length === 5 && segments[2] === "document" && segments[4] === "section_groups") {
        return { kind: "documentSectionGroups", documentId: segments[3] };
    }
    if (segments.length === 5 && segments[2] === "document" && segments[4] === "rfi") {
        return { kind: "rfiDetail", documentId: segments[3] };
    }
    if (segments.length === 5 && segments[2] === "document" && segments[4] === "submittal") {
        return { kind: "submittalDetail", documentId: segments[3] };
    }
    if (segments.length === 3 && segments[2] === "documents") {
        return isFolderScopedQuery(query) ? { kind: "folderedDocuments" } : { kind: "documentsList" };
    }
    if (segments.length === 3 && segments[2] === "folders") {
        return { kind: "folders" };
    }
    if (segments.length === 3 && segments[2] === "rfis") {
        return { kind: "rfisList" };
    }
    if (segments.length === 3 && segments[2] === "areas") {
        return { kind: "areas" };
    }
    if (segments.length === 3 && segments[2] === "revisions") {
        return { kind: "revisions" };
    }
    // Only the one key `offline_answer.rs::answer_project_setting` ever answers --
    // any other key is recognized-but-never-served there too, so this gate mirrors that
    // exact restriction rather than treating every project-settings key as eligible.
    if (segments.length === 4 && segments[2] === "project-settings" && segments[3] === "disciplineMap") {
        return { kind: "projectSettingDisciplineMap" };
    }
    if (
        segments.length === 6 &&
        segments[2] === "schedule" &&
        segments[3] === "file" &&
        segments[5] === "projects"
    ) {
        return { kind: "scheduleProjects", fileId: segments[4] };
    }
    if (
        segments.length === 6 &&
        segments[2] === "schedule" &&
        segments[3] === "file" &&
        segments[5] === "wbs"
    ) {
        return {
            kind: "scheduleWbs",
            fileId: segments[4],
            projectId: readStringQueryParam(query, "project_id"),
        };
    }
    if (
        segments.length === 6 &&
        segments[2] === "schedule" &&
        segments[3] === "wbs" &&
        segments[5] === "children"
    ) {
        return { kind: "scheduleWbsChildren", wbsId: segments[4] };
    }
    if (segments.length === 3 && segments[2] === "conversation") {
        return { kind: "conversationsList" };
    }
    return null;
}

/** The request-time gate `tryNativeAnswerFirst` checks before ever calling
 *  `answerFromNativeOfflineCache` -- see the module doc comment. */
export function isAnswerFirstEligiblePath(pathname: string, query?: unknown): boolean {
    return classifyAnswerFirstPath(pathname, query) !== null;
}

let activeQueryClient: QueryClient | null = null;

/** Registered by `TenantQueryClientProvider` (Tauri only) so this module can correct
 *  the currently-rendered tenant's cache without importing React or the provider --
 *  see that file's effect. `null` when no tenant client is mounted (signed out, or a
 *  platform where this module's own request-time gate never fires anyway). */
export function registerAnswerFirstQueryClient(queryClient: QueryClient | null): void {
    activeQueryClient = queryClient;
}

const telemetry = { hits: 0, misses: 0, corrections: 0 };

/** Local hit/miss/correction counts, no payloads -- for the dev-window debug log and
 *  tests. */
export function getAnswerFirstTelemetry(): Readonly<{
    hits: number;
    misses: number;
    corrections: number;
}> {
    return telemetry;
}

/** `performance.mark`, not `startupTrace.ts`'s `markStartup` -- a hit/miss fires on
 *  every eligible request for the life of the session, not just at startup, so routing
 *  it through `markStartup`'s Tauri IPC call (`startup_trace`) would pay a round trip
 *  per request, defeating the whole point of this lane's `isAnswerFirstEligiblePath`
 *  gate. Browser-only, diagnostics-grade: never throws outward, matches `markOpenWork`'s
 *  own defensive style in `startupTrace.ts`. */
function markAnswerFirst(kind: "hit" | "miss"): void {
    try {
        performance.mark(`primepoint:answer-first:${kind}`);
    } catch (cause) {
        console.warn(`nativeAnswerFirst: performance.mark(answer-first:${kind}) failed`, cause);
    }
}

/** Invalidates the React Query cache entries that plausibly read `pathname` -- see the
 *  module doc comment's "Correction" paragraph for why this is a predicate over the
 *  request's own identifying token rather than one exact computed key. No-ops for a
 *  path `classifyAnswerFirstPath` doesn't recognize (defensive; every caller already
 *  gated on `isAnswerFirstEligiblePath` before ever reaching here) or a by-type
 *  correction whose `type` query param is missing/malformed (nothing to key off). */
function invalidateCorrectedQueries(queryClient: QueryClient, pathname: string, query: unknown): void {
    const family = classifyAnswerFirstPath(pathname, query);
    if (!family) return;
    switch (family.kind) {
        case "file":
        case "sheetBootstrap": {
            const { fileId } = family;
            void queryClient.invalidateQueries({
                predicate: (activeQuery) =>
                    Array.isArray(activeQuery.queryKey) && activeQuery.queryKey.includes(fileId),
            });
            return;
        }
        case "document":
        case "documentSectionGroups": {
            const { documentId } = family;
            void queryClient.invalidateQueries({
                predicate: (activeQuery) =>
                    Array.isArray(activeQuery.queryKey) && activeQuery.queryKey.includes(documentId),
            });
            return;
        }
        case "sectionGroupsByType": {
            const docType = readStringQueryParam(query, "type");
            if (!docType) return;
            // Matches both `PrimeQueryKeys.sectionGroupsByDocType(docType)` and
            // `PrimeQueryKeys.documentsByType(docType)` -- `useSectionGroupQueries`'s
            // `fetchSectionGroupCollection` seeds the latter as a side effect of the
            // exact request this correction is about, so both need the same refresh.
            void queryClient.invalidateQueries({
                predicate: (activeQuery) =>
                    Array.isArray(activeQuery.queryKey) && activeQuery.queryKey.includes(docType),
            });
            return;
        }
        case "documentsList":
            void queryClient.invalidateQueries(PrimeQueryKeys.documents());
            return;
        case "folders":
            void queryClient.invalidateQueries(PrimeQueryKeys.folders);
            return;
        case "folderedDocuments":
            void queryClient.invalidateQueries(PrimeQueryKeys.documentsFolderedRoot);
            return;
        case "rfisList":
            void queryClient.invalidateQueries(PrimeQueryKeys.rfis());
            return;
        case "rfiDetail":
            void queryClient.invalidateQueries(PrimeQueryKeys.rfiDetail(family.documentId));
            return;
        case "submittalDetail":
            void queryClient.invalidateQueries(PrimeQueryKeys.submittalDetail(family.documentId));
            return;
        case "areas":
            void queryClient.invalidateQueries(PrimeQueryKeys.areas);
            return;
        case "revisions": {
            const documentId = readStringQueryParam(query, "document_id");
            if (!documentId) return;
            void queryClient.invalidateQueries({
                predicate: (activeQuery) =>
                    Array.isArray(activeQuery.queryKey) && activeQuery.queryKey.includes(documentId),
            });
            return;
        }
        case "projectSettingDisciplineMap":
            void queryClient.invalidateQueries(PrimeQueryKeys.projectSettingsDisciplineMap);
            return;
        case "scheduleProjects":
        case "scheduleWbs":
            void queryClient.invalidateQueries(PrimeQueryKeys.scheduleActivities(family.fileId));
            return;
        case "scheduleWbsChildren":
            void queryClient.invalidateQueries({
                predicate: (activeQuery) =>
                    Array.isArray(activeQuery.queryKey) && activeQuery.queryKey.includes(family.wbsId),
            });
            return;
        case "conversationsList":
            void queryClient.invalidateQueries(PrimeQueryKeys.allConversations);
            return;
    }
}

/** Cache keys given a network-only pass for their NEXT request, consumed the first
 *  time `tryNativeAnswerFirst` sees them again (whichever request that turns out to
 *  be). Set by `reviseInBackground` right before it schedules a correction: without
 *  this, the invalidated query's own refetch re-enters this same seam, hits the SAME
 *  still-unchanged DAO row, gets served the SAME stale answer, disagrees with the
 *  network again, and re-invalidates -- forever. One-shot rather than a standing
 *  bypass because once that one real network response lands the correction is done;
 *  the DAO itself catches up on its own from the next sync/population run. */
const networkOnlyOnce = new Set<string>();

/** Test-only: clears the module-level `networkOnlyOnce` set between test cases that
 *  reuse the same request shape (and so the same `_nativeOfflineCacheKey`) as an
 *  earlier one. Production code never calls this -- the set drains itself as
 *  `tryNativeAnswerFirst` consumes each entry. */
export function __resetNetworkOnlyOnceForTests(): void {
    networkOnlyOnce.clear();
}

let correctionScheduled = false;
const pendingCorrections: { pathname: string; query: unknown }[] = [];

function scheduleCorrection(pathname: string, query: unknown): void {
    telemetry.corrections += 1;
    pendingCorrections.push({ pathname, query });
    if (correctionScheduled) return;
    correctionScheduled = true;
    void Promise.resolve().then(() => {
        correctionScheduled = false;
        const corrections = pendingCorrections.splice(0, pendingCorrections.length);
        const queryClient = activeQueryClient;
        if (!queryClient) return;
        for (const correction of corrections) {
            invalidateCorrectedQueries(queryClient, correction.pathname, correction.query);
        }
        if (isDebugEnabled()) {
            console.log(
                "[axios] Answer-first: network disagreed with a local answer, corrected matching queries",
            );
        }
    });
}

/** Key-order-independent structural equality for two JSON-shaped values. Both sides
 *  here are always plain JSON round-tripped through Tauri IPC or an HTTP JSON body, so
 *  this never needs the extra cases `axios.ts`'s own `stableSerialize` (built for
 *  cache *keys*, a different job) handles -- Date, URLSearchParams. */
function stableJson(value: unknown): string {
    if (value === null || typeof value !== "object") return JSON.stringify(value);
    if (Array.isArray(value)) return `[${value.map(stableJson).join(",")}]`;
    return `{${Object.entries(value as Record<string, unknown>)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, item]) => `${JSON.stringify(key)}:${stableJson(item)}`)
        .join(",")}}`;
}

function jsonEqual(a: unknown, b: unknown): boolean {
    return stableJson(a) === stableJson(b);
}

function buildLocalAnswerResponse(config: AxiosRequestConfig, data: unknown): AxiosResponse {
    return {
        data,
        status: 200,
        statusText: "OK (local answer)",
        headers: new AxiosHeaders({ [LOCAL_ANSWER_HEADER]: "true" }),
        config,
        request: undefined,
    } as AxiosResponse;
}

/**
 * Issues the real request in the background using the SAME adapter the caller would
 * otherwise have gotten (`undefined` in production, resolving to axios's normal
 * transport; a test's own mock adapter in tests) -- never the local-answer override,
 * which exists only on the config object this closure already captured. Never rejects
 * outward: a background failure (including one the generic offline fallback itself
 * resolves instead of rejecting) just means the instant local answer already served is
 * the end of the story for this request -- no error UI, matching the sync rulings.
 */
async function reviseInBackground(
    axiosInstance: AxiosInstance,
    originalConfig: NativeAnswerFirstConfig,
    pathname: string,
    query: unknown,
    localAnswer: unknown,
    realAdapter: NativeAnswerFirstConfig["adapter"],
    generation: number,
    prefs: PrefsCapability,
): Promise<void> {
    const backgroundConfig: NativeAnswerFirstConfig = {
        ...originalConfig,
        adapter: realAdapter,
        _nativeAnswerFirstBackgroundRefetch: true,
    };
    try {
        const response = await axiosInstance(backgroundConfig);
        // An org switch that lands while this background revalidation is itself in
        // flight must never correct the NEW tenant's query cache using a comparison
        // made for the OLD one -- same fence as the local-answer install below, and
        // for the same reason (finding #1: the generation captured before this
        // request started is the only thing that can catch a switch mid-await).
        if (!prefs.isGenerationCurrent(generation)) return;
        if (!jsonEqual(response.data, localAnswer)) {
            const cacheKey = originalConfig._nativeOfflineCacheKey;
            if (cacheKey) networkOnlyOnce.add(cacheKey);
            scheduleCorrection(pathname, query);
        }
    } catch {
        // Network still down, or a real rejection -- the local answer already
        // satisfied the caller.
    }
}

/**
 * The request-time hook. Mutates `config` in place when the local DAO can answer it:
 * installs a per-request `adapter` override that resolves instantly with the local
 * answer, and kicks off the real request in the background (never awaited by the
 * caller -- the returned promise here only covers the local DAO lookup itself). No-op
 * (config untouched, request proceeds exactly as it did before this lane) when:
 * no offline answer capability exists, this IS itself a background revalidation request
 * (recursion guard), not a family `nativeGetCacheKey` would cache in the first place,
 * the path is not one `offline_answer` can answer (`isAnswerFirstEligiblePath` is checked
 * before the IPC call), or the DAO has nothing for it.
 */
export async function tryNativeAnswerFirst(
    config: NativeAnswerFirstConfig,
    nativeUrl: URL | null,
    axiosInstance: AxiosInstance,
): Promise<void> {
    const { prefs, offline } = platformRef.current;
    const answer = offline?.answer;
    if (!answer) return;
    if (config._nativeAnswerFirstBackgroundRefetch) return;
    if (!nativeUrl || !config._nativeOfflineCacheKey) return;
    const query = answerFirstQuery(nativeUrl, config.params);
    if (!isAnswerFirstEligiblePath(nativeUrl.pathname, query)) return;
    if (networkOnlyOnce.delete(config._nativeOfflineCacheKey)) {
        // A prior answer-first hit for this exact request was just corrected by the
        // network (reviseInBackground, below) -- force this one refetch straight to
        // the real network instead of re-serving the same still-stale DAO row, which
        // would just disagree again and re-invalidate forever.
        telemetry.misses += 1;
        markAnswerFirst("miss");
        return;
    }

    // The request interceptor captured this generation before the mirror call.
    // If the organization changes during the call, do not install the answer.
    const generation = config._nativeOfflineGeneration;
    if (generation === undefined) return;

    const localAnswer = await answer.answer({
        method: config.method ?? "get",
        path: nativeUrl.pathname,
        query,
    });

    if (localAnswer === null) {
        telemetry.misses += 1;
        markAnswerFirst("miss");
        return;
    }
    if (!prefs.isGenerationCurrent(generation)) {
        // The tenant this request started under is no longer the active one --
        // fall through to the real network request exactly as a miss would,
        // rather than answering from a DAO that may now belong to a different
        // organization.
        telemetry.misses += 1;
        markAnswerFirst("miss");
        return;
    }
    telemetry.hits += 1;
    markAnswerFirst("hit");
    if (isDebugEnabled()) {
        console.log(`[axios] Answer-first: local hit for GET ${nativeUrl.pathname}`);
    }

    const realAdapter = config.adapter;
    config.adapter = async (cfg) => buildLocalAnswerResponse(cfg, localAnswer);
    void reviseInBackground(
        axiosInstance,
        config,
        nativeUrl.pathname,
        query,
        localAnswer,
        realAdapter,
        generation,
        prefs,
    );
}
